import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  Modal,
  Snackbar,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import ChatAgentMessageInput from "@components/ChatAgent/components/ChatAgentMessageInput/ChatAgentMessageInput";
import ChatAgentMessageBox from "@components/ChatAgent/components/ChatAgentMessageBox/ChatAgentMessageBox";
import ChatAgentSidebar from "@components/ChatAgent/components/ChatAgentSidebar/ChatAgentSidebar";
import ChatAgentFeedbackDialog, {
  FeedbackPayload,
} from "@components/ChatAgent/components/ChatAgentFeedbackDialog/ChatAgentFeedbackDialog";
import { X, Menu, Info } from "lucide-react";
import { useAuth } from "@context/auth";
import { AxiosError } from "axios";
import ChatAgentErrorOverlay from "@components/ChatAgent/components/ChatAgentErrorOverlay/ChatAgentErrorOverlay";
import { getCookie } from "@utils/utilsCookie";
import { useTranslation } from "react-i18next";
import {
  ChatColumn,
  CloseButton,
  DisclaimerText,
  HeaderAIIcon,
  HeaderLeft,
  HeaderSubtitle,
  HeaderTextContainer,
  HeaderTitle,
  InputFooterContainer,
  LoadingHistoryContainer,
  LoadingHistoryMessage,
  MessagesContainer,
  MobileMenuButton,
  ModalContainer,
  ModalHeaderContainer,
  RootContainer,
  SessionTerminatedMessage,
  SessionTerminatedMessageContainer,
} from "@components/ChatAgent/style";
import {
  AGENT_API_PREFIX,
  AGENT_BASE_URLS,
  AgentType,
  agentSupportsHistory,
  buildAgentUrl,
  buildConversationPreview,
  useChatAgent,
} from "@hooks/useChatAgent";
import { getTimestamp } from "@utils/utilsDate";
import dayjs from "dayjs";
import ChatAgentAgentSelector from "@components/ChatAgent/components/ChatAgentAgentSelector/ChatAgentAgentSelector";
import { axiosInstance } from "src/services/config";

export interface ChatAgentRequestPayload {
  session_id: string;
  session_attributes: {
    corporate_id: string;
  };
  payload: {
    end_session: boolean;
    user_message: string;
  };
}

export interface ChatAgentMessage {
  response: string;
  fist_message: boolean;
  end_session: boolean;
  sender: "user" | "assistant";
  from_history?: boolean;
  timestamp?: string;
  suggested_questions?: string[];
  pending?: boolean;
}

export interface ChatAgentResponse {
  text?: string;
  suggested_questions?: string[];
}

const formatTimestampFromIso = (iso?: string) => {
  if (!iso) return undefined;
  const d = dayjs(iso);
  return d.isValid() ? d.format("HH:mm") : undefined;
};

const MAX_UPLOAD_SIZE = 5 * 1024 * 1024;

const UPLOAD_CONTENT_TYPES: Record<string, string> = {
  csv: "text/csv",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xls: "application/vnd.ms-excel",
};

const getFileExtension = (filename: string) =>
  filename.split(".").pop()?.toLowerCase() ?? "";

const ChatAgent = () => {
  const { user } = useAuth();
  const { t } = useTranslation("agent_chat");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [messages, setMessages] = useState<ChatAgentMessage[]>([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [isRetrievingHistory, setIsRetrievingHistory] = useState(false);
  const [blockUserInput, setBlockUserInput] = useState(false);
  const [apiError, setApiError] = useState("");
  const [feedbackTargetIndex, setFeedbackTargetIndex] = useState<number | null>(
    null,
  );
  const [adminAutoPromptTrigger, setAdminAutoPromptTrigger] = useState(0);
  const adminAutoPromptSentRef = useRef(0);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);
  const [showLegalDisclaimer, setShowLegalDisclaimer] = useState(true);

  const {
    isOpen,
    closeChat: onClose,
    sessionId,
    corporateId,
    agentType,
    selectAgentType,
    conversations,
    isLoadingConversations,
    selectConversation,
    newConversation,
    deleteConversation,
    updateConversationMeta,
    bumpConversationToTop,
    removeConversationLocal,
    providerError,
    clearProviderError,
    isCreatingConversation,
    isFreshSession,
    clearFreshSession,
    freshSessionId,
  } = useChatAgent();

  const baseUrl = agentType ? AGENT_BASE_URLS[agentType] : undefined;
  const hasHistory = agentSupportsHistory(agentType);

  const scrollToLastMessage = useCallback(() => {
    if (messagesContainerRef && messagesContainerRef.current) {
      setTimeout(() => {
        messagesContainerRef.current!.scrollTo({
          top: messagesContainerRef.current!.scrollHeight,
          behavior: "auto",
        });
      }, 300);
    }
  }, []);

  const initChat = () => {
    setApiError("");
    setUploadError("");
    clearProviderError();
    setBlockUserInput(false);
    setMessages([]);
  };

  const getChatHistory = useCallback(() => {
    if (!agentType || !sessionId || !corporateId) return;
    const endpoint = buildAgentUrl(agentType, `messages/${sessionId}`);
    if (!endpoint) return;
    setIsRetrievingHistory(true);

    axiosInstance
      .get(`/${AGENT_API_PREFIX[agentType]}/messages/${sessionId}`, {
        params: { corporate_id: corporateId },
        baseURL: baseUrl,
      })
      .then((res) => {
        const items = res.data?.messages ?? [];
        if (items.length > 0) {
          const mapped: ChatAgentMessage[] = items.map((message: any) => ({
            sender: message.role === "user" ? "user" : "assistant",
            response: message.text,
            fist_message: false,
            end_session: false,
            from_history: true,
            timestamp: formatTimestampFromIso(message.timestamp),
          }));
          setMessages(mapped);
          if (hasHistory) {
            const firstUser = items.find((m: any) => m.role === "user");
            const lastAssistant = [...items]
              .reverse()
              .find((m: any) => m.role !== "user");
            updateConversationMeta(sessionId, {
              title: firstUser?.text?.slice(0, 60),
              preview: buildConversationPreview(
                lastAssistant?.text,
                t("previewDiagram") || "Diagram",
              ),
              message_count: items.length,
              updated_at:
                items[items.length - 1]?.timestamp ?? new Date().toISOString(),
            });
          }
          scrollToLastMessage();
        } else {
          setMessages([]);
        }
      })
      .catch((error: AxiosError) => {
        if (error?.response?.status !== 404) {
          console.error(
            "[ChatAgent] An error occurred while retrieving agent chat history",
            error,
          );
          setApiError(t("historyUnavailable") || "");
        }
        setMessages([]);
      })
      .finally(() => {
        setIsRetrievingHistory(false);
      });
  }, [
    agentType,
    hasHistory,
    sessionId,
    corporateId,
    scrollToLastMessage,
    updateConversationMeta,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    initChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionId]);

  useEffect(() => {
    if (!isOpen || !agentType) return;
    if (!baseUrl) {
      setApiError(t("agentNotConfigured") || "");
      return;
    }
    if (sessionId) {
      if (isFreshSession) {
        clearFreshSession();
      } else {
        getChatHistory();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, agentType, sessionId]);

  useEffect(() => {
    if (!isOpen || !providerError) return;
    setApiError(t(providerError) || providerError);
  }, [isOpen, providerError, t]);

  useEffect(() => {
    if (!blockUserInput && messageInputRef.current)
      messageInputRef.current.focus();
  }, [blockUserInput]);

  useEffect(() => {
    if (
      !messagesContainerRef.current ||
      messages.length === 0 ||
      messages.filter((m) => !m.from_history).length === 0
    ) {
      return;
    }
    const lastMessage = messages[messages.length - 1];
    if (
      lastMessage.sender === "user" &&
      !lastMessage.from_history &&
      !lastMessage.pending
    ) {
      setMessages((prev) => [
        ...prev,
        {
          sender: "assistant",
          end_session: false,
          fist_message: false,
          response: "",
        },
      ]);
    } else if (
      lastMessage.sender === "assistant" &&
      lastMessage.response === ""
    ) {
      retrieveMessageFromAgent();
    }
    scrollToLastMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  const sendMessage = async (message: string, displayMessage?: string) => {
    if (!agentType || !baseUrl || !sessionId) return;
    const endpoint = buildAgentUrl(agentType, "message");
    if (!endpoint) return;

    const display = displayMessage ?? message;

    const payload: ChatAgentRequestPayload = {
      session_id: sessionId,
      session_attributes: {
        corporate_id: corporateId,
      },
      payload: {
        end_session: false,
        user_message: message,
      },
    };

    setBlockUserInput(true);
    setIsSendingMessage(true);

    const isFirstUserMessage = !messages.some((m) => m.sender === "user");

    setMessages((prev) => [
      ...prev,
      {
        sender: "user",
        end_session: false,
        fist_message: prev.length === 0,
        response: display,
        timestamp: getTimestamp(),
        pending: true,
      },
    ]);

    if (hasHistory) {
      if (isFirstUserMessage) {
        bumpConversationToTop(sessionId, {
          title: display.slice(0, 60),
        });
      } else {
        bumpConversationToTop(sessionId);
      }
    }

    axiosInstance
      .post(`/${AGENT_API_PREFIX[agentType]}/message`, payload, {
        baseURL: baseUrl,
      })
      .then(() => {
        setMessages((prev) => {
          const lastIdx = prev.length - 1;
          const last = prev[lastIdx];
          if (!last || last.sender !== "user" || !last.pending) return prev;
          const updated = prev.map((m, i) =>
            i === lastIdx ? { ...m, pending: false } : m,
          );
          return [
            ...updated,
            {
              sender: "assistant",
              end_session: false,
              fist_message: false,
              response: "",
            },
          ];
        });
      })
      .catch((error) => {
        let rolledBack = false;
        setMessages((prev) => {
          if (!prev.some((m) => m.pending)) return prev;
          rolledBack = true;
          return prev.filter((m) => !m.pending);
        });
        if (!rolledBack) return;
        if (hasHistory && isFirstUserMessage) {
          removeConversationLocal(sessionId);
        }
        console.error(
          "[ChatAgent] An error occurred sending message to agent",
          error,
        );
        if (
          error.response &&
          error.response.data &&
          error.response.data.response
        ) {
          setApiError(error.response.data.response as string);
        } else if (error.message) {
          setApiError(`${error.message} [${error.code}]`);
        } else {
          setApiError(t("sendMessageFailed") || "");
        }
      })
      .finally(() => {
        setIsSendingMessage(false);
      });
  };

  useEffect(() => {
    if (
      adminAutoPromptTrigger > adminAutoPromptSentRef.current &&
      agentType === "admin" &&
      sessionId &&
      baseUrl
    ) {
      adminAutoPromptSentRef.current = adminAutoPromptTrigger;
      sendMessage(t("agentTypeAdmin"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminAutoPromptTrigger, agentType, sessionId, baseUrl]);

  const requestUploadUrl = async (
    filename: string,
  ): Promise<{ upload_url: string; s3_key: string }> => {
    const res = await axiosInstance.post(
      `/${AGENT_API_PREFIX[agentType!]}/upload-url`,
      { filename, session_id: sessionId },
      { params: { corporate_id: corporateId }, baseURL: baseUrl },
    );
    return res.data;
  };

  const putFileToS3 = (uploadUrl: string, file: File, contentType: string) =>
    fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": contentType },
      body: file,
    });

  const uploadFileAndNotify = async (file: File) => {
    if (!agentType || !baseUrl || !sessionId) return;

    const extension = getFileExtension(file.name);
    const contentType = UPLOAD_CONTENT_TYPES[extension];
    if (!contentType) {
      setUploadError(t("fileWrongFormat") || "");
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE) {
      setUploadError(t("fileTooLarge") || "");
      return;
    }

    setIsUploadingFile(true);
    try {
      let { upload_url, s3_key } = await requestUploadUrl(file.name).catch(
        (error) => {
          console.error("[ChatAgent] Failed to request upload URL", error);
          throw new Error("uploadUrlFailed");
        },
      );

      let putResponse = await putFileToS3(upload_url, file, contentType);

      // URL scaduto (TTL 1h): richiede una nuova presigned URL e riprova una volta.
      if (putResponse.status === 403) {
        ({ upload_url, s3_key } = await requestUploadUrl(file.name));
        putResponse = await putFileToS3(upload_url, file, contentType);
        if (putResponse.status === 403) {
          setUploadError(t("uploadExpiredRetry") || "");
          return;
        }
      }

      if (!putResponse.ok) {
        setUploadError(t("uploadToS3Failed") || "");
        return;
      }

      const payloadMessage =
        t("fileUploadedMessage", { filename: file.name, key: s3_key }) ||
        `${file.name} (${s3_key})`;
      const bubbleMessage =
        t("fileUploadedBubble", { filename: file.name }) || file.name;
      sendMessage(payloadMessage, bubbleMessage);
    } catch (error) {
      console.error("[ChatAgent] File upload failed", error);
      setUploadError(
        (error instanceof Error && error.message === "uploadUrlFailed"
          ? t("uploadUrlFailed")
          : t("uploadToS3Failed")) || "",
      );
    } finally {
      setIsUploadingFile(false);
    }
  };

  const retrieveMessageFromAgent = async () => {
    if (!agentType || !baseUrl || !sessionId) return;
    const streamBase = buildAgentUrl(agentType, "response/stream");
    if (!streamBase) return;
    const endpoint = `${streamBase}?session_id=${sessionId}&corporate_id=${corporateId}`;

    try {
      const response = await fetch(endpoint, {
        method: "GET",
        cache: "no-cache",
        headers: {
          Authorization: `Bearer ${user?.authToken}`,
          "x-ada-session-token": getCookie("sessionId"),
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`Stream request failed with status ${response.status}`);
      }
      if (!response.body) {
        throw new Error("Stream body not available");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let buffer = "";
      let assistantText = "";
      let inErrorEvent = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split("\n");
        buffer = lines.pop() ?? "";

        for (const line of lines) {
          if (line.trim() === "") {
            inErrorEvent = false;
            continue;
          }
          if (line.includes("event: error")) {
            inErrorEvent = true;
            setMessages((prev) => {
              const updated = [...prev];
              if (updated.length > 0) {
                updated[updated.length - 1] = {
                  ...updated[updated.length - 1],
                  end_session: true,
                };
              }
              return updated;
            });
          } else if (line.startsWith("data:")) {
            const jsonString = line.replace(/^data:\s*/, "");
            if (inErrorEvent) {
              let detail = "";
              try {
                const parsed = JSON.parse(jsonString);
                detail =
                  (typeof parsed === "string" ? parsed : parsed?.message) ?? "";
              } catch {
                detail = jsonString;
              }
              const baseMsg = t("streamError") || "";
              setApiError(detail ? `${baseMsg} ${detail}` : baseMsg);
              continue;
            }
            try {
              const data: ChatAgentResponse = JSON.parse(jsonString);
              if (data.text) {
                assistantText += data.text;
              }
              setMessages((prev) => {
                const updated = [...prev];
                if (updated.length === 0) return updated;
                const last = { ...updated[updated.length - 1] };
                if (data.text) {
                  last.response = (last.response || "") + data.text;
                }
                if (data.suggested_questions) {
                  last.suggested_questions = data.suggested_questions;
                }
                if (!last.timestamp) {
                  last.timestamp = getTimestamp();
                }
                updated[updated.length - 1] = last;
                return updated;
              });
              scrollToLastMessage();
            } catch (err) {
              console.error(
                "[ChatAgent] An error occurred parsing JSON SSE:",
                err,
              );
            }
          }
        }
      }

      setBlockUserInput(false);
      if (hasHistory) {
        updateConversationMeta(sessionId, {
          updated_at: new Date().toISOString(),
          ...(assistantText
            ? {
                preview: buildConversationPreview(
                  assistantText,
                  t("previewDiagram") || "Diagram",
                ),
              }
            : {}),
        });
      }
    } catch (error: any) {
      console.error(
        "[ChatAgent] An error has occurred while fetching agent's response:",
        error,
      );
      const baseMsg = t("streamError") || "";
      if (
        error.response &&
        error.response.data &&
        error.response.data.response
      ) {
        setApiError(error.response.data.response as string);
      } else if (error.message) {
        setApiError(`${baseMsg} ${error.message}`.trim());
      } else {
        setApiError(baseMsg);
      }
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    if (blockUserInput || apiError) return;
    sendMessage(suggestion);
  };

  const submitFeedback = async (payload: FeedbackPayload) => {
    if (
      !agentType ||
      !baseUrl ||
      !sessionId ||
      !corporateId ||
      feedbackTargetIndex === null
    ) {
      return;
    }
    const target = messages[feedbackTargetIndex];
    if (!target) return;
    const feedbackEndpoint = buildAgentUrl(agentType, "feedback");
    if (!feedbackEndpoint) return;

    axiosInstance
      .put(
        feedbackEndpoint,
        {
          session_id: sessionId,
          message_id: `msg_${feedbackTargetIndex}`,
          corporate_id: corporateId,
          feedback: payload.feedback,
          rating: payload.rating,
          message_preview: target.response?.slice(0, 200),
          comment: payload.comment,
        },
        { baseURL: baseUrl },
      )
      .catch((error) => {
        console.error("[ChatAgent] Failed to submit feedback", error);
      });
  };

  const handleNewChat = async () => {
    if (
      blockUserInput ||
      isSendingMessage ||
      isRetrievingHistory ||
      isCreatingConversation
    )
      return;
    if (isMobile) setMobileSidebarOpen(false);
    newConversation();
  };

  const handleSelectConversation = (sid: string) => {
    if (sid === sessionId) return;
    if (blockUserInput || isSendingMessage || isCreatingConversation) return;
    if (isMobile) setMobileSidebarOpen(false);
    selectConversation(sid);
  };

  const handleDeleteConversation = async (sid: string) => {
    if (blockUserInput || isSendingMessage || isCreatingConversation) return;
    await deleteConversation(sid);
  };

  useEffect(() => {
    if (!isMobile) setMobileSidebarOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!isOpen) setMobileSidebarOpen(false);
  }, [isOpen]);

  const isSessionTerminated = messages.find((message) => message.end_session);
  const lastAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.sender === "assistant" && m.response !== "") return i;
    }
    return -1;
  }, [messages]);

  return (
    <Modal open={isOpen} onClose={onClose}>
      <RootContainer>
        <ModalContainer theme={theme}>
          {!isMobile && (
            <ChatAgentSidebar
              conversations={conversations}
              isLoading={isLoadingConversations}
              activeSessionId={sessionId}
              onSelect={handleSelectConversation}
              onNewChat={handleNewChat}
              onDelete={handleDeleteConversation}
              disabled={
                blockUserInput || isSendingMessage || isCreatingConversation
              }
              historyEnabled={hasHistory}
              awaitingAgentChoice={agentType === null}
            />
          )}

          {isMobile && (
            <AnimatePresence>
              {mobileSidebarOpen && (
                <>
                  <motion.div
                    key="mobile-sidebar-backdrop"
                    onClick={() => setMobileSidebarOpen(false)}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundColor: "rgba(0,0,0,0.45)",
                      zIndex: 10,
                    }}
                  />
                  <motion.div
                    key="mobile-sidebar-panel"
                    initial={{ x: "-100%" }}
                    animate={{ x: 0 }}
                    exit={{ x: "-100%" }}
                    transition={{
                      duration: 0.32,
                      ease: [0.2, 0.8, 0.2, 1],
                    }}
                    style={{
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: "min(85vw, 320px)",
                      zIndex: 11,
                      boxShadow: "0 10px 40px rgba(0,0,0,0.18)",
                      backgroundColor: theme.palette.background.paper,
                      display: "flex",
                    }}
                  >
                    <ChatAgentSidebar
                      conversations={conversations}
                      isLoading={isLoadingConversations}
                      activeSessionId={sessionId}
                      onSelect={handleSelectConversation}
                      onNewChat={handleNewChat}
                      onDelete={handleDeleteConversation}
                      disabled={
                        blockUserInput ||
                        isSendingMessage ||
                        isCreatingConversation
                      }
                      historyEnabled={hasHistory}
                      awaitingAgentChoice={agentType === null}
                    />
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          )}

          <ChatColumn id="chat-column">
            <ModalHeaderContainer>
              <HeaderLeft>
                <Stack display={"flex"} flexDirection={"row"} gap="1rem">
                  <MobileMenuButton
                    onClick={() => setMobileSidebarOpen((prev) => !prev)}
                    aria-label={
                      mobileSidebarOpen
                        ? t("closeSidebar") || "Close sidebar"
                        : t("openSidebar") || "Open sidebar"
                    }
                  >
                    <AnimatePresence initial={false} mode="wait">
                      {mobileSidebarOpen ? (
                        <motion.span
                          key="close"
                          initial={{ rotate: -90, opacity: 0 }}
                          animate={{ rotate: 0, opacity: 1 }}
                          exit={{ rotate: 90, opacity: 0 }}
                          transition={{ duration: 0.18 }}
                          style={{ display: "inline-flex" }}
                        >
                          <X size={22} />
                        </motion.span>
                      ) : (
                        <motion.span
                          key="open"
                          initial={{ rotate: 90, opacity: 0 }}
                          animate={{ rotate: 0, opacity: 1 }}
                          exit={{ rotate: -90, opacity: 0 }}
                          transition={{ duration: 0.18 }}
                          style={{ display: "inline-flex" }}
                        >
                          <Menu size={22} />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </MobileMenuButton>
                  {/* <HeaderAIIcon>AI</HeaderAIIcon>
                  <HeaderTextContainer>
                    <HeaderTitle>{t("title")}</HeaderTitle>
                    <HeaderSubtitle>{t("subtitle")}</HeaderSubtitle>
                  </HeaderTextContainer> */}
                </Stack>
                <Box
                  tabIndex={-1}
                  id={"badge-agent-disclaimer"}
                  data-testid="badge-agent-disclaimer"
                  aria-label={t("badge_aria_label")}
                  sx={{
                    display: showLegalDisclaimer ? "flex" : "none",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0.75rem 1rem",
                    gap: "0.75rem",
                    border: `0.0625rem solid ${theme.palette.primary.main}`,
                    backgroundColor: "#ffb6c4",
                    color: theme.palette.textInput,
                    borderRadius: "0.25rem",
                  }}
                >
                  <Box
                    sx={{
                      width: "2rem",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Info size={"1.5rem"} color={theme.palette.textInput} />
                  </Box>
                  <Typography
                    tabIndex={-1}
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: "400",
                    }}
                    color={theme.palette.textInput}
                  >
                    {t("ai_support_legal_disclaimer")}
                  </Typography>
                  <Box>
                    <X
                      id={"close-legal-disclaimer"}
                      aria-label={t("close") || "close"}
                      onClick={() => setShowLegalDisclaimer(false)}
                      style={{
                        cursor: "pointer",
                      }}
                      size={"1.5rem"}
                      color={theme.palette.textInput}
                    />
                  </Box>
                </Box>
              </HeaderLeft>
              <CloseButton
                onClick={onClose}
                disabled={
                  blockUserInput || isRetrievingHistory || isSendingMessage
                }
                sx={{
                  position: "absolute",
                  top: "1.5rem",
                  right: "1.5rem",
                }}
              >
                <X size={24} />
              </CloseButton>
            </ModalHeaderContainer>

            <MessagesContainer
              id="message-container"
              theme={theme}
              blockUserInput={blockUserInput}
              ref={messagesContainerRef}
              gap={"20px"}
              height={"100%"}
            >
              {(agentType === null ||
                (!!sessionId &&
                  freshSessionId === sessionId &&
                  agentType !== "admin")) && (
                <ChatAgentAgentSelector
                  selectedType={agentType}
                  onSelect={(type) => {
                    selectAgentType(type as AgentType);
                    if (type === "admin") {
                      setAdminAutoPromptTrigger((n) => n + 1);
                    }
                  }}
                  disabled={isCreatingConversation || !!apiError}
                />
              )}

              {messages.map((message, index) => (
                <ChatAgentMessageBox
                  key={`chat_message_${sessionId}_${index}`}
                  message={message}
                  user={user}
                  index={index}
                  onSuggestionClick={handleSuggestionClick}
                  onRateExperience={(i) => setFeedbackTargetIndex(i)}
                  showRateExperience={index === lastAssistantIndex}
                />
              ))}

              {agentType !== null &&
                messages.length === 0 &&
                (isRetrievingHistory ||
                isCreatingConversation ||
                agentType === "admin" ? (
                  <LoadingHistoryContainer>
                    <CircularProgress size={20} />
                    <LoadingHistoryMessage>
                      {isRetrievingHistory
                        ? t("retrieving_history")
                        : t("preparingNewChat")}
                    </LoadingHistoryMessage>
                  </LoadingHistoryContainer>
                ) : freshSessionId === sessionId ? (
                  <ChatAgentMessageBox
                    index={0}
                    message={{
                      sender: "assistant",
                      response: t("agentReadyPrompt") || "",
                      end_session: false,
                      fist_message: false,
                    }}
                    user={user}
                    showRateExperience={false}
                  />
                ) : null)}
            </MessagesContainer>

            <Divider />

            {isSessionTerminated ? (
              <SessionTerminatedMessageContainer>
                <SessionTerminatedMessage>
                  {t("session_terminated")}
                </SessionTerminatedMessage>
              </SessionTerminatedMessageContainer>
            ) : (
              <InputFooterContainer>
                <ChatAgentMessageInput
                  isLoading={isSendingMessage || isCreatingConversation}
                  onSendMessage={(message) => sendMessage(message)}
                  disabled={
                    blockUserInput ||
                    !!apiError ||
                    isCreatingConversation ||
                    agentType === null
                  }
                  inputRef={messageInputRef}
                  showUploadButton={agentType === "admin"}
                  onUploadFile={uploadFileAndNotify}
                  isUploading={isUploadingFile}
                />
                {t("disclaimer") && (
                  <DisclaimerText>{t("disclaimer")}</DisclaimerText>
                )}
              </InputFooterContainer>
            )}
          </ChatColumn>

          {apiError && (
            <ChatAgentErrorOverlay
              error={apiError}
              onReset={initChat}
              onClose={onClose}
            />
          )}
        </ModalContainer>

        <ChatAgentFeedbackDialog
          open={feedbackTargetIndex !== null}
          onClose={() => setFeedbackTargetIndex(null)}
          onSubmit={submitFeedback}
          agentName={t("title") || undefined}
        />

        <Snackbar
          open={!!uploadError}
          autoHideDuration={5000}
          onClose={() => setUploadError("")}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity="error"
            variant="filled"
            onClose={() => setUploadError("")}
            sx={{ borderRadius: "10px", fontWeight: 600 }}
          >
            {uploadError}
          </Alert>
        </Snackbar>
      </RootContainer>
    </Modal>
  );
};

export default ChatAgent;
